/**
 * FP3-130 — Concise Progress Summary (Print).
 *
 * Dense printable roll-up covering one, some, or all current
 * caseload students in a single report. Designed for provider
 * meetings and 3rd-quarter progress checks — students stack
 * continuously; no per-student page break unless the browser
 * decides so. All numbers agree with the on-screen Progress /
 * Student Overview / Goal Progress surfaces because the payload
 * originates from the same canonical fields (see backend
 * /api/progress/report block comment).
 *
 * URL:
 *   /print/progress-summary
 *   ?student_ids=<csv>          (optional — omit = full caseload)
 *   &start=<YYYY-MM-DD>         (optional — default active period start)
 *   &end=<YYYY-MM-DD>           (optional — default today)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  PrintShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";
import { parseDateOnly, formatMonthDayYear } from "@/lib/dateOnly";

// Compact M/D/YY used only for the attended-dates line — matches
// the user's spec example ("8/7/26, 8/14/26, 8/21/26, ..."). Never
// TZ-shifts because we drive it off parseDateOnly.
function shortMDY(iso) {
  const d = parseDateOnly(iso);
  if (!d) return "";
  const yy = String(d.getFullYear() % 100).padStart(2, "0");
  return `${d.getMonth() + 1}/${d.getDate()}/${yy}`;
}

function fmtPct(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(0)}%`;
}

function StudentSection({ student }) {
  const {
    id, first_name, last_name, grade, service_minutes_per_month,
    attended_dates, missed_dates, goals,
  } = student;
  const displayName = `${first_name || ""} ${last_name || ""}`.trim();
  const gradeStr = grade ? `Grade ${grade}` : null;
  // FP3-130C — Monthly service minutes badge in the student header.
  // Reads the canonical `service_minutes_per_month` field the backend
  // resolves via `_caseload_commitment_minutes` (same helper used by
  // /students?sort=commitment). Omitted entirely when the student has
  // no commitment set — never renders "· null min/month".
  const minutesStr =
    Number.isFinite(service_minutes_per_month) && service_minutes_per_month > 0
      ? `${service_minutes_per_month} min/month`
      : null;
  // FP3-130B — Compose the "Missed / not held" line. Each entry is
  // "M/D/YY — <reason label>[ (<note>)]". Provider-unavailable rows
  // append the canonical `provider_unavailable_note` in parentheses
  // ONLY when non-empty (no empty parens). Student-absent rows never
  // carry a reason (no structured absence field exists) so they
  // render as "8/19/26 — Student absent". Multi-entry lines join
  // with "; " so the whole thing wraps naturally on one line.
  const missedLine = (missed_dates || [])
    .map((e) => {
      const dateStr = shortMDY(e.date);
      if (e.reason === "provider_unavailable") {
        const note = (e.note || "").trim();
        return note
          ? `${dateStr} — Provider unavailable (${note})`
          : `${dateStr} — Provider unavailable`;
      }
      if (e.reason === "student_absent") {
        return `${dateStr} — Student absent`;
      }
      return `${dateStr} — ${e.reason}`;
    })
    .join("; ");
  return (
    <section
      className="fp3130-student-section"
      data-testid={`report-student-${id}`}
    >
      <div className="fp3130-student-heading">
        <span className="fp3130-student-name">{displayName}</span>
        {gradeStr && (
          <span className="fp3130-student-grade">— {gradeStr}</span>
        )}
        {minutesStr && (
          <span
            className="fp3130-service-minutes"
            data-testid={`report-service-minutes-${id}`}
          >
            · {minutesStr}
          </span>
        )}
      </div>
      <div
        className="fp3130-attended-line"
        data-testid={`report-attended-${id}`}
      >
        <span className="fp3130-attended-label">Dates attended:</span>{" "}
        {attended_dates && attended_dates.length > 0 ? (
          attended_dates.map(shortMDY).join(", ")
        ) : (
          <em style={{ color: "#777" }}>No attended sessions in reporting period.</em>
        )}
      </div>
      {missedLine && (
        <div
          className="fp3130-missed-line"
          data-testid={`report-missed-${id}`}
        >
          <span className="fp3130-missed-label">Missed / not held:</span>{" "}
          {missedLine}
        </div>
      )}
      {(!goals || goals.length === 0) ? (
        <p
          className="fp3130-no-goals"
          data-testid={`report-no-goals-${id}`}
        >
          No active goals.
        </p>
      ) : (
        <table
          className="fp3130-goals-table"
          data-testid={`report-goals-${id}`}
        >
          <thead>
            <tr>
              <th style={{ width: "70%" }}>Goal</th>
              <th style={{ width: "30%" }}>Current Progress</th>
            </tr>
          </thead>
          <tbody>
            {goals.map((g) => {
              // FP3-130A — Canonical Goal Average (mean of per-session
              // percentages). "—" when the goal has no numeric session
              // data yet; never the stale `current_mastery` snapshot.
              const hasData =
                g.canonical_average !== null &&
                g.canonical_average !== undefined;
              return (
                <tr key={g.id} data-testid={`report-goal-${g.id}`}>
                  <td className="fp3130-goal-text">{g.goal_text || "—"}</td>
                  <td className="fp3130-progress-cell">
                    <strong>{fmtPct(g.canonical_average)}</strong>
                    {" "}<span className="fp3130-target-slash">/ {fmtPct(g.target_mastery)}</span>
                    {hasData && g.canonical_session_count ? (
                      <>
                        {" "}
                        <span className="fp3130-session-count">
                          · {g.canonical_session_count}{" "}
                          session{g.canonical_session_count === 1 ? "" : "s"}
                        </span>
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function PrintCaseloadProgressReport() {
  const [params] = useSearchParams();
  const studentIds = params.get("student_ids") || "";
  const startParam = params.get("start") || "";
  const endParam = params.get("end") || "";
  // FP3-130A — Optional scope filter (direct | all). Only meaningful
  // when `student_ids` is empty (manual selection always wins).
  const scopeParam = params.get("scope") || "";

  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);

  // FP3-130 — StrictMode-safe fetch guard. React 18's dev double-
  // invoke of effects would otherwise fire /api/progress/report
  // twice on mount (the payload IS batched — same URL both times —
  // but a spurious duplicate network hit is a code smell). We
  // memoize the last query signature and skip identical repeats.
  // An AbortController from the cleanup path would also work; we
  // pick this ref approach because the network response drives a
  // single state transition — cancelling mid-flight offers no UX
  // benefit here.
  const lastKeyRef = useRef(null);

  useEffect(() => {
    const key = `${studentIds}|${startParam}|${endParam}|${scopeParam}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    const q = {};
    if (studentIds) q.student_ids = studentIds;
    if (startParam) q.start_date = startParam;
    if (endParam) q.end_date = endParam;
    if (scopeParam && !studentIds) q.scope = scopeParam;
    api
      .getProgressReport(q)
      .then((r) => {
        setReport(r.data);
        setPhase("ready");
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    // Intentionally no cleanup — the ref-based dedup makes cancel
    // unnecessary AND cancellation would swallow the fetch response
    // in StrictMode's second-mount cleanup (which fires while the
    // real first-mount fetch is still in flight). If the caller
    // navigates away, React unmounts the component and setState
    // becomes a no-op harmlessly.
  }, [studentIds, startParam, endParam, scopeParam]);

  const providerName = useMemo(() => {
    const p = report?.provider;
    if (!p) return "";
    return `${p.first_name || ""} ${p.last_name || ""}`.trim();
  }, [report]);

  const schoolName =
    report?.organization?.school_name || report?.organization?.name || "";

  const periodStr = useMemo(() => {
    if (!report?.period) return "";
    const s = formatMonthDayYear(report.period.start_date);
    const e = formatMonthDayYear(report.period.end_date);
    if (s && e) return `${s} – ${e}`;
    return s || e || "";
  }, [report]);

  const subtitle = useMemo(() => {
    const bits = [];
    if (providerName) bits.push(providerName);
    if (schoolName) bits.push(schoolName);
    if (periodStr) bits.push(periodStr);
    return bits.join(" · ");
  }, [providerName, schoolName, periodStr]);

  const students = report?.students || [];

  return (
    <PrintShell
      title="Progress Summary"
      subtitle={subtitle}
      testId="print-progress-report"
    >
      <PrintAccessGate phase={phase} error={error}>
        {report && (
          <div className="fp3130-report" data-testid="progress-report-body">
            {students.length === 0 ? (
              <p className="fp-print-empty" style={{ padding: "12px 16px" }}>
                No students in report.
              </p>
            ) : (
              students.map((s) => (
                <StudentSection key={s.id} student={s} />
              ))
            )}
          </div>
        )}
      </PrintAccessGate>
      {/* Report-specific dense typography — scoped to this route
          and layered on top of print.css primitives. */}
      <style>{`
        .fp3130-report { color: var(--fp-print-ink, #171612); }
        .fp3130-student-section {
          padding: 8px 0 10px;
          border-top: 1px solid var(--fp-print-line, #d8d3c8);
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .fp3130-student-section:first-child { border-top: 0; }
        .fp3130-student-heading {
          font-size: 12.5pt;
          font-weight: 600;
          margin-bottom: 4px;
          letter-spacing: -0.01em;
        }
        .fp3130-student-name { color: #171612; }
        .fp3130-student-grade {
          color: #6b665a;
          font-weight: 500;
          font-size: 11pt;
          margin-left: 2px;
        }
        .fp3130-service-minutes {
          color: #6b665a;
          font-weight: 500;
          font-size: 11pt;
          margin-left: 4px;
        }
        .fp3130-attended-line {
          font-size: 10pt;
          color: #3f3b32;
          line-height: 1.35;
          margin: 2px 0 6px;
        }
        .fp3130-attended-label {
          color: #6b665a;
          font-weight: 600;
          margin-right: 2px;
        }
        .fp3130-missed-line {
          font-size: 10pt;
          color: #3f3b32;
          line-height: 1.35;
          margin: 0 0 6px;
        }
        .fp3130-missed-label {
          color: #6b665a;
          font-weight: 600;
          margin-right: 2px;
        }
        .fp3130-no-goals {
          font-size: 10pt;
          color: #6b665a;
          font-style: italic;
          margin: 4px 0 2px;
        }
        .fp3130-goals-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10pt;
          margin-top: 2px;
        }
        .fp3130-goals-table thead th {
          text-align: left;
          font-size: 9pt;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #6b665a;
          font-weight: 600;
          padding: 4px 6px 3px;
          border-bottom: 1px solid var(--fp-print-line, #d8d3c8);
        }
        .fp3130-goals-table tbody td {
          padding: 4px 6px;
          vertical-align: top;
          border-bottom: 1px solid #ece8dd;
          line-height: 1.35;
        }
        .fp3130-goal-text {
          color: #171612;
          font-weight: 400;
          word-break: break-word;
        }
        .fp3130-progress-cell { white-space: nowrap; }
        .fp3130-target-slash { color: #6b665a; font-weight: 400; }
        .fp3130-session-count {
          color: #6b665a;
          font-weight: 400;
          font-size: 9pt;
        }
        @media print {
          .fp3130-goals-table tbody tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </PrintShell>
  );
}
